import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, MapPin, Building2, FileText, Clock, CheckCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Header from '../components/Header';
import Footer from '../components/Footer';
import locationsData from '../data/locations-data.json';

/**
 * Pagină SEO location-based pentru certificat constatator per oraș
 * URL: /certificat-constatator-bucuresti, /certificat-constatator-cluj-napoca, etc.
 */

function LocationPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Datele orașului sunt disponibile INSTANT (import static, nu fetch async)
  const cityData = locationsData.cities.find(c => c.slug === slug);

  // Link-uri interne intre orase (SEO): acelasi judet + urmatoarele 8 orase din lista (rotatie)
  const allCities = locationsData.cities;
  const cityIndex = allCities.findIndex(c => c.slug === slug);
  const sameCountyCities = cityData
    ? allCities.filter(c => c.county === cityData.county && c.slug !== slug).slice(0, 8)
    : [];
  const otherCities = cityIndex === -1
    ? []
    : Array.from({ length: Math.min(8, allCities.length - 1) }, (_, i) => allCities[(cityIndex + 1 + i) % allCities.length])
        .filter(c => !sameCountyCities.some(s => s.slug === c.slug));

  const [city] = useState(cityData || null);
  const [content, setContent] = useState('');
  const [contentLoading, setContentLoading] = useState(!!cityData);
  const [notFound] = useState(!cityData);

  // Încarcă doar conținutul Markdown (async - e ok, restul paginii e deja vizibil)
  useEffect(() => {
    if (!cityData) return;

    const loadContent = async () => {
      try {
        const contentResponse = await fetch(`/seo-data/content/${slug}.md`);
        if (!contentResponse.ok) {
          throw new Error('Conținut nu există');
        }

        const markdown = await contentResponse.text();

        // Extrage conținut fără frontmatter
        const contentWithoutFrontmatter = markdown.replace(/^---[\s\S]*?---\n/, '');

        setContent(contentWithoutFrontmatter);
      } catch (error) {
        console.error('Error loading city content:', error);
      } finally {
        setContentLoading(false);
      }
    };

    loadContent();
  }, [slug, cityData]);

  // SEO helper values
  const pageTitle = city ? `Certificat Constatator ${city.name} | Constatatoare.ro` : 'Certificat Constatator Online | Constatatoare.ro';
  const metaDescription = city ? `Certificat constatator ${city.name} 100% online, fără portaluri greoaie. 87 lei + TVA, primit pe email în câteva minute. Comandă acum!` : '';
  const canonicalUrl = `https://constatatoare.ro/certificat-constatator/${slug}/`;
  const ogImageUrl = 'https://constatatoare.ro/og-image-v2.jpg';

  if (notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-cyan-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Orașul nu a fost găsit
          </h1>
          <p className="text-gray-600 mb-8">
            Pagina pentru acest oraș nu există încă.
          </p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Înapoi la comandă
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-cyan-50">
      {/* SEO Meta Tags cu React Helmet */}
      {city && (
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={metaDescription} />
          <link rel="canonical" href={canonicalUrl} />

          {/* Open Graph / Facebook / WhatsApp */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:title" content={`Certificat Constatator ${city.name} - Online în 5 Minute`} />
          <meta property="og:description" content={metaDescription} />
          <meta property="og:image" content={ogImageUrl} />
          <meta property="og:locale" content="ro_RO" />
          <meta property="og:site_name" content="Constatatoare.ro" />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`Certificat Constatator ${city.name} - Online în 5 Minute`} />
          <meta name="twitter:description" content={metaDescription} />
          <meta name="twitter:image" content={ogImageUrl} />

          {/* Service + BreadcrumbList JSON-LD */}
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Service",
                "name": `Certificat Constatator Online ${city.name}`,
                "description": metaDescription,
                "url": canonicalUrl,
                "provider": { "@type": "Organization", "@id": "https://constatatoare.ro/#organization", "name": "Constatatoare.ro", "url": "https://constatatoare.ro/" },
                "serviceType": "Certificat Constatator ONRC",
                "areaServed": {
                  "@type": "City",
                  "name": city.name,
                  "containedInPlace": {
                    "@type": "Country",
                    "name": "Romania"
                  }
                },
                "offers": [
                  {
                    "@type": "Offer",
                    "name": "Certificat Constatator",
                    "price": "99",
                    "priceCurrency": "RON",
                    "availability": "https://schema.org/InStock",
                    "url": "https://constatatoare.ro"
                  },
                  {
                    "@type": "Offer",
                    "name": "Certificat cu Istoric",
                    "price": "449",
                    "priceCurrency": "RON",
                    "availability": "https://schema.org/InStock",
                    "url": "https://constatatoare.ro"
                  }
                ]
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Acasa",
                    "item": "https://constatatoare.ro"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": city.name,
                    "item": canonicalUrl
                  }
                ]
              }
            ]
          })}</script>
        </Helmet>
      )}

      <Header />

      {/* Hero Section cu breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-8">
          {/* Breadcrumb */}
          <nav className="text-sm mb-4">
            <ol className="flex items-center space-x-2 text-gray-600">
              <li>
                <Link to="/" className="hover:text-emerald-600 transition">
                  Acasă
                </Link>
              </li>
              <li>/</li>
              <li className="text-gray-900 font-medium">{city.name}</li>
            </ol>
          </nav>

          {/* Title cu icon */}
          <div className="flex items-center space-x-3 mb-4">
            <MapPin className="h-8 w-8 text-emerald-600" />
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Certificat Constatator {city.name}
            </h1>
          </div>

          {/* Subtitle cu date oraș */}
          <p className="text-lg text-gray-600 flex items-center flex-wrap gap-4">
            <span className="flex items-center">
              <Building2 className="h-5 w-5 mr-2 text-emerald-600" />
              {city.stats.companies}
            </span>
            <span className="text-gray-400">•</span>
            <span>{city.population} de locuitori</span>
            <span className="text-gray-400">•</span>
            <span>{city.county}</span>
          </p>
        </div>
      </div>

      {/* Main Content - Layout 2 coloane */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-[1fr_350px] gap-8">
          {/* Coloană stângă - Conținut SEO */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* CTA top - înainte de articol */}
            <div className="mb-8 p-6 bg-emerald-50 rounded-xl border border-emerald-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <CheckCircle className="h-6 w-6 text-emerald-600 mr-2" />
                Comandă acum certificatul tău
              </h3>
              <p className="text-gray-700 mb-4">
                Procesul durează doar 5 minute. Livrare instant prin email, fără cont.
              </p>
              <button
                onClick={() => navigate('/')}
                className="w-full sm:w-auto px-8 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition shadow-lg"
              >
                Comandă certificat constatator
              </button>
            </div>

            {/* Conținut articol */}
            {contentLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
            )}
            <div className="prose prose-lg max-w-none">
              <ReactMarkdown
                className="text-gray-700 leading-relaxed"
                components={{
                  h2: ({node, children, ...props}) => <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4" {...props}>{children}</h2>,
                  p: ({node, children, ...props}) => {
                    // Boldează keyword-ul în paragrafe
                    if (typeof children === 'string' && city) {
                      const keywords = [
                        `certificat constatator ${city.name}`,
                        `certificatul constatator ${city.name}`,
                        `certificat constatator pentru ${city.name}`
                      ];

                      let processedText = children;
                      keywords.forEach(keyword => {
                        const regex = new RegExp(`(${keyword})`, 'gi');
                        processedText = processedText.replace(regex, '**$1**');
                      });

                      return <ReactMarkdown className="mb-4">{processedText}</ReactMarkdown>;
                    }
                    return <p className="mb-4" {...props}>{children}</p>;
                  }
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          </div>

          {/* Coloană dreapta - Sidebar cu features */}
          <div className="space-y-6">
            {/* Card avantaje */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                De ce să comanzi online?
              </h3>
              <ul className="space-y-3">
                {[
                  { icon: Clock, text: 'Procesare în 5 minute' },
                  { icon: FileText, text: 'Document oficial valabil' },
                  { icon: MapPin, text: `Disponibil pentru firmele din ${city.name}` },
                  { icon: CheckCircle, text: 'Livrare gratuită prin email' }
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <item.icon className="h-5 w-5 text-emerald-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Card industrii locale */}
            <div className="bg-gradient-to-br from-emerald-600 to-cyan-600 rounded-2xl shadow-lg p-6 text-white">
              <h3 className="text-xl font-bold mb-4">
                Industrii principale
              </h3>
              <div className="flex flex-wrap gap-2">
                {city.industries.map((industry, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium"
                  >
                    {industry}
                  </span>
                ))}
              </div>
            </div>

            {/* Card cartiere */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Zone acoperite
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                Serviciu disponibil pentru toate cartierele din {city.name}:
              </p>
              <div className="flex flex-wrap gap-2">
                {city.neighborhoods.slice(0, 6).map((neighborhood, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium border border-emerald-200"
                  >
                    {neighborhood}
                  </span>
                ))}
                {city.neighborhoods.length > 6 && (
                  <span className="px-3 py-1 text-gray-500 text-sm">
                    +{city.neighborhoods.length - 6} zone
                  </span>
                )}
              </div>
            </div>

            {/* Link-uri interne: orase din acelasi judet */}
            {sameCountyCities.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Alte orașe din județul {city.county}
                </h3>
                <ul className="space-y-2">
                  {sameCountyCities.map((c) => (
                    <li key={c.slug}>
                      <a href={`/certificat-constatator/${c.slug}/`} className="text-emerald-700 hover:text-emerald-900 hover:underline text-sm">
                        Certificat constatator {c.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Link-uri interne: urmatoarele orase din lista (rotatie - fiecare oras primeste link de la alte 8) */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Certificat constatator în alte orașe
              </h3>
              <ul className="space-y-2">
                {otherCities.map((c) => (
                  <li key={c.slug}>
                    <a href={`/certificat-constatator/${c.slug}/`} className="text-emerald-700 hover:text-emerald-900 hover:underline text-sm">
                      Certificat constatator {c.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default LocationPage;
